/** Firebase Storage folder for event image assets (flat list). */
export const EVENTS_STORAGE_FOLDER = 'events';

const FILENAME_SAFE = /^[a-z0-9._-]+$/;

/**
 * Basename only: strip path, lowercase, spaces → underscores, drop invalid chars.
 * Must end with .png and match safe charset.
 * @param {string} rawName
 * @returns {string | null} null if empty or invalid after sanitization
 */
export function sanitizeEventImageFilename(rawName) {
  if (!rawName || typeof rawName !== 'string') return null;
  let base = rawName.trim().split(/[/\\]/).pop() || '';
  if (!base) return null;
  base = base.toLowerCase().replace(/\s+/g, '_');
  if (!base.endsWith('.png')) {
    return null;
  }
  if (!FILENAME_SAFE.test(base)) {
    return null;
  }
  const withoutExt = base.slice(0, -4);
  if (!withoutExt) {
    return null;
  }
  return base;
}

/**
 * PNG only; no pixel or file-size limits (Firebase Storage quota still applies).
 * @param {File} file
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateEventImageFile(file) {
  if (!file || !(file instanceof File)) {
    return { ok: false, message: 'Please choose a file to upload.' };
  }
  if (file.type !== 'image/png') {
    return {
      ok: false,
      message: 'Only PNG files are allowed. Save your image as a PNG and try again.',
    };
  }
  return { ok: true };
}

/**
 * Map Firestore `image` (filename) to a download URL from the Storage listing.
 *
 * @param {Record<string, string>} imageUrlByName
 * @param {string} [imageName]
 * @returns {string | undefined}
 */
export function resolveStoredImageUrl(imageUrlByName, imageName) {
  const key = (imageName ?? '').trim();
  if (!key || !imageUrlByName) return undefined;
  if (imageUrlByName[key]) return imageUrlByName[key];
  const lower = key.toLowerCase();
  for (const [k, v] of Object.entries(imageUrlByName)) {
    if (k.toLowerCase() === lower) {
      return v;
    }
  }
  return undefined;
}

/**
 * Storage basename for the list card (`image`; falls back to mistaken `cardImage`).
 * @param {Record<string, unknown>} [event]
 * @returns {string}
 */
export function eventCardImageFilename(event) {
  const fromImage = String(event?.image ?? '').trim();
  if (fromImage) return fromImage;
  return String(event?.cardImage ?? '').trim();
}

/**
 * Storage basename for the detail header (falls back to legacy `image`).
 * @param {Record<string, unknown>} [event]
 * @returns {string}
 */
export function eventHeaderImageFilename(event) {
  const header = String(event?.headerImage ?? '').trim();
  if (header) return header;
  return String(event?.image ?? '').trim();
}
